/**
 * /terminos — Términos y Condiciones.
 *
 * Linked from the cart drawer's "Acepto los términos" checkbox plus the
 * site footer. The content is plain prose; legal review can update it
 * without touching the layout.
 */

import { LegalSection, LegalShell } from '@/components/layout/LegalShell';
import { contact } from '@/lib/contact';

export function TermsPage() {
  return (
    <LegalShell eyebrow="Legal" title="Términos y condiciones" updated="Última actualización: mayo de 2026">
      <LegalSection title="1. Servicio y proveedor">
        <p>
          AlphaConcreto es una plataforma SaaS de cálculo estructural de concreto armado, basada en
          la <strong>Norma Peruana E.060</strong> y el <strong>ACI 318-19</strong>, operada por{' '}
          <strong>{contact.legalName}</strong>
          {contact.legalRuc && (
            <>
              {' '}(RUC <span className="tabular">{contact.legalRuc}</span>)
            </>
          )}
          , con operación en {contact.legalAddress}.
        </p>
        <p>
          El usuario es responsable de verificar los resultados con criterio profesional antes de
          aplicarlos en obra. AlphaConcreto <em>no reemplaza</em> el criterio de un ingeniero
          estructural colegiado, ni asume responsabilidad sobre los proyectos finales del usuario.
        </p>
      </LegalSection>

      <LegalSection title="2. Cuentas y elegibilidad">
        <p>
          Para usar la plataforma debes tener al menos 18 años o, si eres menor, contar con la
          autorización de tus padres o tutores. Eres responsable de mantener la confidencialidad de
          tu contraseña y de todas las actividades realizadas bajo tu cuenta.
        </p>
      </LegalSection>

      <LegalSection title="3. Fierros y suscripciones">
        <p>
          Los <strong>fierros</strong> son créditos internos de uso. Cada exportación de PDF o Excel
          consume entre 1 y 2 fierros. Los fierros pueden adquirirse en packs únicos o mediante una
          suscripción mensual o anual que otorga exportaciones ilimitadas durante el periodo
          contratado.
        </p>
        <p>
          Los fierros <strong>no son reembolsables</strong> ni convertibles en dinero, pero{' '}
          <strong>no caducan</strong> mientras la cuenta permanezca activa.
        </p>
      </LegalSection>

      <LegalSection title="4. Bono mensual">
        <p>
          Cada cuenta activa recibe automáticamente 15 fierros gratis al registrarse y 15 fierros
          adicionales cada 30 días de actividad. El bono no es acumulable y no se otorga a cuentas
          inactivas por más de 12 meses.
        </p>
      </LegalSection>

      <LegalSection title="5. Pagos">
        <p>
          Los pagos se procesan mediante <strong>Culqi</strong> (Niubiz / VisaNet). AlphaConcreto no
          almacena los datos de tarjeta — únicamente recibe un identificador de cargo. Los precios
          se muestran en Soles Peruanos (PEN) e incluyen el IGV aplicable.
        </p>
      </LegalSection>

      <LegalSection title="6. Cancelación y reembolsos">
        <p>
          Puedes cancelar tu suscripción en cualquier momento desde tu panel; los servicios
          continuarán activos hasta el final del periodo ya pagado. Las condiciones específicas de
          reembolso están detalladas en nuestra{' '}
          <a href="/reembolsos" className="underline-offset-2 hover:underline" style={{ color: 'var(--accent)' }}>
            Política de reembolsos
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="7. Propiedad intelectual">
        <p>
          Todo el software, marca, diseño y código fuente de AlphaConcreto son propiedad de Alpha+ o
          de sus licenciantes. El acceso al servicio se concede bajo licencia, no se transfiere
          propiedad. Los <em>cálculos y memorias que generes</em> son tuyos.
        </p>
      </LegalSection>

      <LegalSection title="8. Limitación de responsabilidad">
        <p>
          AlphaConcreto se ofrece "tal cual". Aunque ponemos el máximo cuidado en la implementación
          de fórmulas y verificaciones, no garantizamos ausencia de errores. La responsabilidad
          máxima en caso de falla del servicio se limita al monto pagado por el usuario en los
          últimos 30 días.
        </p>
      </LegalSection>

      <LegalSection title="9. Ley aplicable">
        <p>
          Estos términos se rigen por la legislación peruana. Cualquier controversia será sometida a
          los jueces de Lima Cercado, sin perjuicio del derecho del consumidor a recurrir a Indecopi
          mediante nuestro{' '}
          <a href="/reclamaciones" className="underline-offset-2 hover:underline" style={{ color: 'var(--accent)' }}>
            Libro de Reclamaciones
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="10. Contacto">
        <p>Para consultas legales o de soporte puedes escribirnos o llamarnos:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            Correo:{' '}
            <a
              href={`mailto:${contact.supportEmail}`}
              className="underline-offset-2 hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              {contact.supportEmail}
            </a>
          </li>
          <li>
            Teléfono / WhatsApp:{' '}
            <a
              href={`tel:${contact.phoneTel}`}
              className="underline-offset-2 hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              {contact.phoneDisplay}
            </a>{' '}
            ({contact.supportHours})
          </li>
          <li>
            Más canales en{' '}
            <a
              href="/contacto"
              className="underline-offset-2 hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              /contacto
            </a>
            .
          </li>
        </ul>
      </LegalSection>
    </LegalShell>
  );
}
